// src/products/dataroom/components/Modals/ProjectColorModal.js
import React, { useState } from 'react';
import { X, Palette, Check } from 'lucide-react';
import { supabase } from '../../../../config';

const ProjectColorModal = ({ 
  isOpen, 
  onClose, 
  project,
  onColorChanged 
}) => {
  const [selectedColor, setSelectedColor] = useState(project?.color || '#3B82F6');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Color palette
  const colorPalette = [
    { name: 'Blue', value: '#3B82F6', bg: 'bg-blue-500' },
    { name: 'Purple', value: '#8B5CF6', bg: 'bg-purple-500' },
    { name: 'Green', value: '#10B981', bg: 'bg-green-500' },
    { name: 'Red', value: '#EF4444', bg: 'bg-red-500' },
    { name: 'Orange', value: '#F97316', bg: 'bg-orange-500' },
    { name: 'Pink', value: '#EC4899', bg: 'bg-pink-500' },
    { name: 'Indigo', value: '#6366F1', bg: 'bg-indigo-500' },
    { name: 'Teal', value: '#14B8A6', bg: 'bg-teal-500' },
    { name: 'Yellow', value: '#F59E0B', bg: 'bg-yellow-500' },
    { name: 'Gray', value: '#6B7280', bg: 'bg-gray-500' },
    { name: 'Emerald', value: '#059669', bg: 'bg-emerald-500' },
    { name: 'Rose', value: '#F43F5E', bg: 'bg-rose-500' }
  ];

  const validateHexColor = (color) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const { error: updateError } = await supabase
        .from('dataroom_projects')
        .update({ 
          color: selectedColor,
          updated_at: new Date().toISOString()
        })
        .eq('id', project.id);

      if (updateError) throw updateError;

      // Log the color change
      await supabase
        .from('dataroom_logs')
        .insert({
          user_id: (await supabase.auth.getUser()).data.user?.id,
          project_id: project.id,
          action: 'project_updated',
          details: {
            field: 'color',
            old_value: project.color,
            new_value: selectedColor
          }
        });

      onColorChanged && onColorChanged(selectedColor);
      onClose();
    } catch (error) {
      console.error('Error updating project color:', error);
      setError('Failed to update project color. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    setError(null);
  };

  const handleCustomColorChange = (e) => {
    const color = e.target.value;
    setSelectedColor(color);
    setError(null);
  };

  const handleCustomColorInput = (e) => {
    let color = e.target.value;
    
    // Add # if missing
    if (!color.startsWith('#')) {
      color = '#' + color;
    }
    
    // Validate and set color
    if (validateHexColor(color) || color === '#') {
      setSelectedColor(color);
      setError(null);
    } else {
      setError('Please enter a valid hex color (e.g., #FF0000)');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <Palette className="h-6 w-6 text-blue-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">Choose Project Color</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={saving}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4">
              Select a color to help identify "{project?.project_name}" in your dataroom list
            </p>
            
            {/* Color Preview */}
            <div className="flex items-center space-x-3 mb-6">
              <div 
                className="w-16 h-16 rounded-lg border-2 border-gray-200 flex items-center justify-center transition-colors"
                style={{ backgroundColor: selectedColor }}
              >
                <span className="text-white font-medium text-sm drop-shadow-sm">
                  {project?.project_name?.substring(0, 2)?.toUpperCase() || 'PR'}
                </span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{project?.project_name}</p>
                <p className="text-sm text-gray-500">Color: {selectedColor}</p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Color Grid */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Preset Colors</h4>
            <div className="grid grid-cols-6 gap-3">
              {colorPalette.map((color) => (
                <button
                  key={color.value}
                  onClick={() => handleColorSelect(color.value)}
                  className={`w-12 h-12 rounded-lg border-2 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    selectedColor === color.value 
                      ? 'border-gray-800 ring-2 ring-gray-800 ring-offset-2' 
                      : 'border-gray-200 hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                  disabled={saving}
                >
                  {selectedColor === color.value && (
                    <Check className="h-6 w-6 text-white mx-auto drop-shadow" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Color Input */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Custom Color</h4>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={selectedColor}
                onChange={handleCustomColorChange}
                className="w-12 h-12 border border-gray-300 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={saving}
                title="Pick a custom color"
              />
              <input
                type="text"
                value={selectedColor}
                onChange={(e) => handleCustomColorInput(e)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="#3B82F6"
                disabled={saving}
                maxLength={7}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Enter a hex color code (e.g., #FF0000 for red)</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !validateHexColor(selectedColor)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center transition-colors"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Palette className="h-4 w-4 mr-2" />
                Save Color
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectColorModal;